import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal } from 'react-bootstrap';
import swal from 'sweetalert';
import InvoiceService from '../../../services/InvoiceService';
import TaxService from '../../../services/TaxService';
import AuthApiService from '../../../services/AuthApiService';
import { PageHeader, Card, Button, Badge, StatTile, Field, Input, Select, Table, Tr, Td, EmptyState } from '../../ui';

const money = (c, cur = 'cad') => new Intl.NumberFormat('en-CA', { style: 'currency', currency: (cur || 'cad').toUpperCase() }).format((c || 0) / 100);
const date = (d) => (d ? new Date(d).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' }) : '—');
const statusTone = { paid: 'good', issued: 'warn', draft: 'neutral', void: 'neutral', refunded: 'neutral' };

const blankItem = () => ({ description: '', quantity: 1, unit_dollars: '' });

const AdminInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [q, setQ] = useState('');
  const [rates, setRates] = useState([]);
  const [users, setUsers] = useState([]);
  const [msg, setMsg] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(null);

  const flash = (ok, text) => { setMsg({ ok, text }); setTimeout(() => setMsg(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await InvoiceService.adminList({ status: statusFilter, q: q || undefined });
      setInvoices(data.invoices); setStats(data.stats);
    } catch { flash(false, 'Failed to load invoices'); }
    finally { setLoading(false); }
  }, [statusFilter, q]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    TaxService.adminList().then((d) => setRates(d.rates.filter((r) => r.is_active))).catch(() => {});
    AuthApiService.getAllUsers?.().then((r) => setUsers(r?.data || r?.users || [])).catch(() => {});
  }, []);

  const openNew = () => {
    setForm({
      user_id: '', bill_to_name: '', bill_to_email: '', bill_to_address: '',
      items: [blankItem()], tax_rate_ids: rates.map((r) => r.id), notes: '', due_date: '',
    });
    setShowNew(true);
  };

  const preview = useMemo(() => {
    if (!form) return { subtotal: 0, tax: 0, total: 0 };
    const subtotal = form.items.reduce((s, it) => s + Math.round((parseFloat(it.unit_dollars) || 0) * 100) * (parseInt(it.quantity, 10) || 1), 0);
    const tax = rates.filter((r) => form.tax_rate_ids.includes(r.id))
      .reduce((s, r) => s + (r.type === 'fixed' ? Math.round(r.rate) : Math.round(subtotal * r.rate / 100)), 0);
    return { subtotal, tax, total: subtotal + tax };
  }, [form, rates]);

  const submit = async (mode) => {
    setCreating(true);
    try {
      const payload = {
        user_id: form.user_id || null,
        bill_to_name: form.bill_to_name || null,
        bill_to_email: form.bill_to_email || null,
        bill_to_address: form.bill_to_address || null,
        line_items: form.items
          .filter((it) => it.description && it.unit_dollars !== '')
          .map((it) => ({ description: it.description, quantity: parseInt(it.quantity, 10) || 1, unit_amount: Math.round((parseFloat(it.unit_dollars) || 0) * 100) })),
        tax_rate_ids: form.tax_rate_ids,
        notes: form.notes || null,
        due_date: form.due_date || null,
        status: mode === 'draft' ? 'draft' : 'issued',
        mark_paid: mode === 'paid',
        send: mode === 'send',
      };
      if (!payload.line_items.length) { flash(false, 'Add at least one line item'); setCreating(false); return; }
      await InvoiceService.createManual(payload);
      setShowNew(false);
      flash(true, mode === 'send' ? 'Invoice issued and emailed.' : 'Invoice created.');
      load();
    } catch (e) { flash(false, e.response?.data?.error || 'Failed to create invoice'); }
    finally { setCreating(false); }
  };

  const rowAction = async (inv, action) => {
    try {
      if (action === 'send') { const r = await InvoiceService.send(inv.id); flash(r.success, r.message || (r.success ? 'Emailed' : 'Send failed')); }
      if (action === 'paid') { await InvoiceService.markPaid(inv.id); flash(true, 'Marked paid'); }
      if (action === 'void') {
        const ok = await swal({ title: 'Void this invoice?', text: inv.invoice_number, icon: 'warning', buttons: ['Cancel', 'Void'], dangerMode: true });
        if (!ok) return;
        await InvoiceService.voidInvoice(inv.id); flash(true, 'Voided');
      }
      load();
    } catch (e) { flash(false, e.response?.data?.error || 'Action failed'); }
  };

  const setItem = (i, k, v) => setForm((f) => ({ ...f, items: f.items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)) }));

  return (
    <div className="tw-mx-auto tw-max-w-5xl">
      <PageHeader
        title="Invoices"
        subtitle="Issued under Siman Tech. Automatic on every payment; create manual invoices here."
        actions={<Button icon="fa-plus" onClick={openNew}>New invoice</Button>}
      />

      {msg && (
        <div className={`tw-mb-5 tw-rounded-xl tw-border tw-px-4 tw-py-3 tw-text-sm ${msg.ok ? 'tw-border-good/30 tw-bg-good/10 tw-text-good' : 'tw-border-live/30 tw-bg-live/10 tw-text-live'}`}>{msg.text}</div>
      )}

      {stats && (
        <div className="tw-mb-6 tw-grid tw-gap-4 sm:tw-grid-cols-3">
          <StatTile icon="fa-file-invoice-dollar" label="Total invoices" value={stats.count} />
          <StatTile icon="fa-circle-check" label="Collected" value={money(stats.paid_total)} tone="good" />
          <StatTile icon="fa-clock" label={`Outstanding (${stats.outstanding_count})`} value={money(stats.outstanding_total)} tone="warn" />
        </div>
      )}

      <Card noPad>
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-border-b tw-border-line tw-p-3">
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="tw-w-40">
            {['all', 'issued', 'paid', 'draft', 'void'].map((s) => <option key={s} value={s}>{s === 'all' ? 'All statuses' : s}</option>)}
          </Select>
          <Input placeholder="Search number / customer…" value={q} onChange={(e) => setQ(e.target.value)} className="tw-max-w-xs" />
        </div>
        {loading ? (
          <div className="tw-py-10 tw-text-center"><i className="fa-solid fa-spinner fa-spin tw-text-brand" /></div>
        ) : invoices.length === 0 ? (
          <EmptyState icon="fa-file-invoice" title="No invoices" hint="They appear here after a payment, or create one manually." />
        ) : (
          <Table head={['Number', 'Customer', 'Issued', 'Total', 'Status', '']}>
            {invoices.map((inv) => (
              <Tr key={inv.id}>
                <Td className="tw-font-mono tw-text-xs tw-font-semibold tw-text-heading">{inv.invoice_number}</Td>
                <Td>{inv.bill_to_name || inv.username || '—'}<div className="tw-text-xs tw-text-content-faint">{inv.bill_to_email}</div></Td>
                <Td className="tw-text-content-muted">{date(inv.issue_date)}</Td>
                <Td className="tw-font-semibold tw-text-heading">{money(inv.total, inv.currency)}</Td>
                <Td><Badge tone={statusTone[inv.status] || 'neutral'}>{inv.status}</Badge>{inv.source === 'manual' && <span className="tw-ml-1.5 tw-text-[10px] tw-text-content-faint">manual</span>}</Td>
                <Td className="tw-text-right tw-whitespace-nowrap">
                  <Button variant="ghost" size="sm" icon="fa-file-pdf" as="a" href={InvoiceService.invoicePdfUrl(inv.id)} target="_blank" rel="noreferrer" />
                  {inv.status !== 'void' && <Button variant="ghost" size="sm" icon="fa-paper-plane" onClick={() => rowAction(inv, 'send')} title="Email to customer" />}
                  {inv.status === 'issued' && <Button variant="ghost" size="sm" icon="fa-circle-check" onClick={() => rowAction(inv, 'paid')} title="Mark paid" />}
                  {inv.status !== 'void' && inv.status !== 'paid' && <Button variant="ghost" size="sm" icon="fa-ban" onClick={() => rowAction(inv, 'void')} title="Void" />}
                </Td>
              </Tr>
            ))}
          </Table>
        )}
      </Card>

      <Modal show={showNew} onHide={() => setShowNew(false)} centered size="lg" scrollable>
        <Modal.Header closeButton><Modal.Title>New invoice</Modal.Title></Modal.Header>
        <Modal.Body>
          {form && (
            <div className="tw-space-y-4">
              <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
                <Field label="Existing customer" hint="Or fill the bill-to fields below">
                  <Select value={form.user_id} onChange={(e) => {
                    const u = users.find((x) => String(x.id) === e.target.value);
                    setForm((f) => ({ ...f, user_id: e.target.value, bill_to_name: u ? (u.full_name || u.username) : f.bill_to_name, bill_to_email: u ? u.email : f.bill_to_email }));
                  }}>
                    <option value="">— none —</option>
                    {users.map((u) => <option key={u.id} value={u.id}>{u.username} ({u.email})</option>)}
                  </Select>
                </Field>
                <Field label="Due date"><Input type="date" value={form.due_date} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} /></Field>
                <Field label="Bill to — name"><Input value={form.bill_to_name} onChange={(e) => setForm((f) => ({ ...f, bill_to_name: e.target.value }))} /></Field>
                <Field label="Bill to — email"><Input value={form.bill_to_email} onChange={(e) => setForm((f) => ({ ...f, bill_to_email: e.target.value }))} /></Field>
              </div>
              <Field label="Bill to — address"><Input value={form.bill_to_address} onChange={(e) => setForm((f) => ({ ...f, bill_to_address: e.target.value }))} /></Field>

              <div>
                <div className="tw-mb-2 tw-text-sm tw-font-semibold tw-text-content">Line items</div>
                {form.items.map((it, i) => (
                  <div key={i} className="tw-mb-2 tw-flex tw-gap-2">
                    <Input placeholder="Description" value={it.description} onChange={(e) => setItem(i, 'description', e.target.value)} className="tw-flex-1" />
                    <Input type="number" min="1" value={it.quantity} onChange={(e) => setItem(i, 'quantity', e.target.value)} className="tw-w-16" />
                    <Input type="number" step="0.01" placeholder="0.00" value={it.unit_dollars} onChange={(e) => setItem(i, 'unit_dollars', e.target.value)} className="tw-w-28" />
                    <Button variant="ghost" size="sm" icon="fa-trash" onClick={() => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }))} />
                  </div>
                ))}
                <Button variant="secondary" size="sm" icon="fa-plus" onClick={() => setForm((f) => ({ ...f, items: [...f.items, blankItem()] }))}>Add line</Button>
              </div>

              {rates.length > 0 && (
                <Field label="Apply taxes / charges">
                  <div className="tw-flex tw-flex-wrap tw-gap-2">
                    {rates.map((r) => {
                      const on = form.tax_rate_ids.includes(r.id);
                      return (
                        <button key={r.id} type="button"
                          onClick={() => setForm((f) => ({ ...f, tax_rate_ids: on ? f.tax_rate_ids.filter((x) => x !== r.id) : [...f.tax_rate_ids, r.id] }))}
                          className={`tw-rounded-lg tw-border tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium ${on ? 'tw-border-brand tw-bg-brand/10 tw-text-brand' : 'tw-border-line tw-text-content-muted'}`}>
                          <i className={`fa-solid ${on ? 'fa-square-check' : 'fa-square'} tw-mr-1`} />{r.name} {r.type === 'percentage' ? `${r.rate}%` : `$${(r.rate / 100).toFixed(2)}`}
                        </button>
                      );
                    })}
                  </div>
                </Field>
              )}

              <Field label="Notes"><Input value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} /></Field>

              <div className="tw-rounded-lg tw-border tw-border-line tw-bg-sunken tw-p-3 tw-text-sm">
                <div className="tw-flex tw-justify-between tw-text-content-muted"><span>Subtotal</span><span>{money(preview.subtotal)}</span></div>
                <div className="tw-flex tw-justify-between tw-text-content-muted"><span>Tax / charges</span><span>{money(preview.tax)}</span></div>
                <div className="tw-mt-1 tw-flex tw-justify-between tw-font-bold tw-text-heading"><span>Total</span><span>{money(preview.total)}</span></div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNew(false)}>Cancel</Button>
          <Button variant="outline" onClick={() => submit('draft')} disabled={creating}>Save draft</Button>
          <Button variant="outline" onClick={() => submit('issue')} disabled={creating}>Issue</Button>
          <Button onClick={() => submit('send')} disabled={creating}>{creating ? 'Working…' : 'Issue & email'}</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminInvoices;
